// CSV-Parsing für den Mitglieder-Import.
//
// Der frühere Parser splittete stur an "," — damit scheiterte jeder deutsche
// Excel-Export (Semikolon als Trennzeichen, UTF-8-BOM, Felder in Anführungszeichen)
// und lieferte still 0 Zeilen zurück. Deshalb hier: Trennzeichen erkennen, BOM
// entfernen, RFC-4180-Quoting unterstützen und Fehler *melden* statt zu schlucken.

export interface CsvParseError {
  /** 1-basierte Zeilennummer in der Datei (Header = 1). */
  line: number;
  message: string;
}

export interface CsvParseResult<T> {
  rows: T[];
  errors: CsvParseError[];
  /** Erkanntes Trennzeichen — nützlich für die UI-Rückmeldung. */
  delimiter: string;
  /** Header-Spalten, die wir nicht kennen (werden ignoriert, aber angezeigt). */
  unknownColumns: string[];
}

const DELIMITERS = [',', ';', '\t', '|'] as const;

/** Wählt das Trennzeichen, das in der Kopfzeile außerhalb von Quotes am häufigsten vorkommt. */
export function detectDelimiter(headerLine: string): string {
  let best: string = ',';
  let bestCount = 0;
  for (const d of DELIMITERS) {
    const count = splitLine(headerLine, d).length - 1;
    if (count > bestCount) {
      best = d;
      bestCount = count;
    }
  }
  return best;
}

/** Zerlegt eine Zeile und respektiert dabei "gequotete" Felder inkl. verdoppelter Quotes (""). */
function splitLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      out.push(field);
      field = '';
    } else {
      field += ch;
    }
  }
  out.push(field);
  return out;
}

/**
 * Zerlegt CSV-Text in Zeilen. Zeilenumbrüche *innerhalb* von Anführungszeichen
 * beenden keinen Datensatz (z.B. mehrzeilige Notizen).
 */
function splitRecords(text: string): string[] {
  const records: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
      continue;
    }
    if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      records.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.length > 0) records.push(current);
  return records.filter((r) => r.trim().length > 0);
}

/** Die Felder, in die ein Import münden kann — Reihenfolge = Anzeige-Reihenfolge im Mapping-UI. */
export const MEMBER_IMPORT_FIELDS = [
  { key: 'name', label: 'Name (voll)' },
  { key: 'firstName', label: 'Vorname' },
  { key: 'lastName', label: 'Nachname' },
  { key: 'company', label: 'Firma' },
  { key: 'role', label: 'Position' },
  { key: 'industry', label: 'Branche' },
  { key: 'email', label: 'E-Mail' },
  { key: 'phone', label: 'Telefon' },
  { key: 'website', label: 'Website' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'street', label: 'Straße' },
  { key: 'zip', label: 'PLZ' },
  { key: 'city', label: 'Stadt' },
  { key: 'country', label: 'Land' },
  { key: 'tags', label: 'Tags' },
  { key: 'notes', label: 'Notizen' },
] as const;

export type MemberImportField = (typeof MEMBER_IMPORT_FIELDS)[number]['key'];

/**
 * Spaltennamen auf unsere Feldnamen abbilden. Neben Deutsch/Englisch sind die
 * Export-Header der gängigen CRMs hinterlegt (HubSpot, Salesforce, Pipedrive,
 * Outlook, Google Kontakte) — deren Dateien laufen damit ohne Handarbeit durch.
 */
const COLUMN_ALIASES: Record<string, MemberImportField> = {
  name: 'name',
  'full name': 'name',
  'person - name': 'name', // Pipedrive
  vorname: 'firstName',
  firstname: 'firstName',
  'first name': 'firstName',
  'given name': 'firstName', // Google
  'person - first name': 'firstName',
  nachname: 'lastName',
  lastname: 'lastName',
  'last name': 'lastName',
  'family name': 'lastName', // Google
  surname: 'lastName',
  'person - last name': 'lastName',
  company: 'company',
  firma: 'company',
  unternehmen: 'company',
  'company name': 'company', // HubSpot
  'account name': 'company', // Salesforce
  organisation: 'company',
  organization: 'company',
  'organization - name': 'company', // Pipedrive
  'organization 1 - name': 'company', // Google
  role: 'role',
  rolle: 'role',
  position: 'role',
  title: 'role', // Salesforce
  'job title': 'role', // HubSpot/Outlook
  'organization 1 - title': 'role', // Google
  industry: 'industry',
  branche: 'industry',
  email: 'email',
  'e-mail': 'email',
  mail: 'email',
  'email address': 'email',
  'e-mail address': 'email', // Outlook
  'person - email': 'email', // Pipedrive
  'e-mail 1 - value': 'email', // Google
  phone: 'phone',
  telefon: 'phone',
  tel: 'phone',
  handy: 'phone',
  mobil: 'phone',
  mobile: 'phone',
  mobilephone: 'phone', // Salesforce
  'phone number': 'phone', // HubSpot
  'business phone': 'phone', // Outlook
  'mobile phone': 'phone',
  'person - phone': 'phone', // Pipedrive
  'phone 1 - value': 'phone', // Google
  website: 'website',
  webseite: 'website',
  url: 'website',
  'website url': 'website', // HubSpot
  'web page': 'website', // Outlook
  linkedin: 'linkedin',
  'linkedin url': 'linkedin',
  'linkedin bio': 'linkedin',
  street: 'street',
  strasse: 'street',
  straße: 'street',
  adresse: 'street',
  address: 'street',
  'street address': 'street', // HubSpot
  mailingstreet: 'street', // Salesforce
  'business street': 'street', // Outlook
  'address 1 - street': 'street', // Google
  zip: 'zip',
  plz: 'zip',
  postleitzahl: 'zip',
  'postal code': 'zip',
  'zip code': 'zip',
  postalcode: 'zip',
  mailingpostalcode: 'zip', // Salesforce
  'business postal code': 'zip', // Outlook
  'address 1 - postal code': 'zip', // Google
  city: 'city',
  stadt: 'city',
  ort: 'city',
  mailingcity: 'city', // Salesforce
  'business city': 'city', // Outlook
  'address 1 - city': 'city', // Google
  country: 'country',
  land: 'country',
  'country/region': 'country', // HubSpot
  mailingcountry: 'country', // Salesforce
  'business country/region': 'country', // Outlook
  'address 1 - country': 'country', // Google
  tags: 'tags',
  schlagworte: 'tags',
  labels: 'tags',
  notes: 'notes',
  notizen: 'notes',
  bemerkung: 'notes',
  bemerkungen: 'notes',
  description: 'notes',
};

export interface ParsedMemberRow {
  name: string;
  company?: string;
  role?: string;
  industry?: string;
  email?: string;
  phone?: string;
  website?: string;
  linkedin?: string;
  street?: string;
  zip?: string;
  city?: string;
  country?: string;
  notes?: string;
  tags?: string[];
}

/** Zerlegt einen Tag-Zellwert — akzeptiert "a|b", "a,b" und "a;b". */
function parseTags(value: string): string[] {
  return value
    .split(/[|,;]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

export interface CsvTable {
  /** Original-Spaltennamen, wie sie in der Datei stehen. */
  header: string[];
  rows: string[][];
  delimiter: string;
}

/** Zerlegt CSV-Text in eine rohe Tabelle — Grundlage für das Spalten-Mapping im Assistenten. */
export function parseCsvTable(text: string): CsvTable | null {
  // UTF-8-BOM entfernen: sonst heißt die erste Spalte "﻿name" und wird nie gefunden.
  const clean = text.replace(/^﻿/, '');
  const records = splitRecords(clean);
  if (records.length === 0) return null;

  const headerLine = records[0] ?? '';
  const delimiter = detectDelimiter(headerLine);
  const header = splitLine(headerLine, delimiter).map((h) => h.trim());
  const rows = records.slice(1).map((r) => splitLine(r, delimiter));
  return { header, rows, delimiter };
}

/** Automatisches Spalten-Mapping über die Alias-Tabelle (inkl. CRM-Presets). */
export function autoMapColumns(header: string[]): (MemberImportField | null)[] {
  const used = new Set<MemberImportField>();
  return header.map((h) => {
    const field = COLUMN_ALIASES[h.trim().toLowerCase()] ?? null;
    // Doppelte Zuordnung (z.B. zwei Telefonspalten) — die erste gewinnt,
    // Ausnahme: tags/notes dürfen theoretisch mehrfach vorkommen, nehmen wir trotzdem nur einmal.
    if (field && used.has(field)) return null;
    if (field) used.add(field);
    return field;
  });
}

/**
 * Baut aus Tabelle + Mapping die Import-Zeilen. Das Mapping kommt aus
 * `autoMapColumns`, der KI-Zuordnung oder der Handkorrektur des Nutzers.
 */
export function buildRowsFromMapping(
  table: CsvTable,
  mapping: (MemberImportField | null)[],
): { rows: ParsedMemberRow[]; errors: CsvParseError[] } {
  const errors: CsvParseError[] = [];
  const rows: ParsedMemberRow[] = [];

  const idx = (field: MemberImportField): number => mapping.indexOf(field);
  const nameIdx = idx('name');
  const firstIdx = idx('firstName');
  const lastIdx = idx('lastName');

  if (nameIdx < 0 && firstIdx < 0 && lastIdx < 0) {
    return {
      rows: [],
      errors: [{ line: 1, message: 'Keine Spalte ist "Name", "Vorname" oder "Nachname" zugeordnet.' }],
    };
  }

  if (table.rows.length === 0) {
    errors.push({ line: 1, message: 'Die Datei enthält nur eine Kopfzeile, keine Daten.' });
  }

  const get = (cols: string[], field: MemberImportField): string | undefined => {
    const i = idx(field);
    if (i < 0) return undefined;
    const v = cols[i]?.trim();
    return v ? v : undefined;
  };

  for (let i = 0; i < table.rows.length; i++) {
    const line = i + 2; // 1-basiert, Header = 1
    const cols = table.rows[i] ?? [];

    const explicitName = nameIdx >= 0 ? cols[nameIdx]?.trim() : undefined;
    const first = get(cols, 'firstName');
    const last = get(cols, 'lastName');
    const name = explicitName || [first, last].filter(Boolean).join(' ').trim();

    if (!name) {
      errors.push({ line, message: 'Zeile übersprungen: kein Name.' });
      continue;
    }

    const tagsRaw = get(cols, 'tags');

    rows.push({
      name,
      company: get(cols, 'company'),
      role: get(cols, 'role'),
      industry: get(cols, 'industry'),
      email: get(cols, 'email'),
      phone: get(cols, 'phone'),
      website: get(cols, 'website'),
      linkedin: get(cols, 'linkedin'),
      street: get(cols, 'street'),
      zip: get(cols, 'zip'),
      city: get(cols, 'city'),
      country: get(cols, 'country'),
      notes: get(cols, 'notes'),
      tags: tagsRaw ? parseTags(tagsRaw) : undefined,
    });
  }

  return { rows, errors };
}

export function parseMembersCsv(text: string): CsvParseResult<ParsedMemberRow> {
  const table = parseCsvTable(text);
  if (!table) {
    return { rows: [], errors: [{ line: 1, message: 'Die Datei ist leer.' }], delimiter: ',', unknownColumns: [] };
  }

  const mapping = autoMapColumns(table.header);
  const unknownColumns = table.header.filter((h, i) => h.trim().length > 0 && !mapping[i]);

  // Ohne Namensspalte ist der Import sinnlos — das muss die UI klar sagen dürfen.
  if (!mapping.includes('name') && !mapping.includes('firstName') && !mapping.includes('lastName')) {
    return {
      rows: [],
      errors: [{
        line: 1,
        message: `Keine Spalte "name" (oder "vorname"/"nachname") gefunden. Gelesene Spalten: ${table.header.join(', ') || '—'}`,
      }],
      delimiter: table.delimiter,
      unknownColumns,
    };
  }

  const { rows, errors } = buildRowsFromMapping(table, mapping);
  return { rows, errors, delimiter: table.delimiter, unknownColumns };
}
